import { AfterViewInit, Component, ViewChild, Input, EventEmitter, Output, OnChanges, OnInit, SimpleChanges, ElementRef } from "@angular/core";
import { NgbDate, NgbCalendar, NgbDateStruct, NgbDatepickerConfig } from "@ng-bootstrap/ng-bootstrap";
import { EnvironmentConfig } from "../../../core/models/environment-config.model";
import { DateRange } from "../../../core/models/date-range.model";

declare var environment: EnvironmentConfig;
@Component({
  selector: "common-date-picker",
  templateUrl: "./date-picker.component.html",
  styleUrls: ["./date-picker.component.scss"],
})
export class DatePickerComponent implements OnInit, OnChanges, AfterViewInit {
  @ViewChild("datepicker") datepicker;
  @ViewChild("datePickerInput") datePickerInput;
  @ViewChild("maxDateTooltip") maxDateTooltip;

  @Input() placeHolder: string;
  @Input() customClass: string = "";
  @Input() showCarrot: boolean = true;
  @Input() showErrorState: boolean;
  @Input() showFocusBorder: boolean;
  @Input() numberOfMonthsToShow: number = 1;
  @Input() numberOfDaysToBlockOff: number = 0;
  @Input() blockWeekends: boolean = false;
  @Input() blockByHours: boolean = false;
  @Input() date: NgbDate | string | any;
  @Input() minDate: NgbDate;
  @Input() maxDate: NgbDate;
  @Input() maxNumberOfDays: number;
  @Input() useAsInput: boolean = true;

  /**
   * When true the inline calendar selects a date *range* instead of a single
   * date: the first click sets the start, a later click sets the end, and any
   * click after a complete range (or on/before the in-progress start) begins a
   * new range. Emits {@link rangeSelected}. Backward compatible — defaults to
   * single-date selection.
   */
  @Input() rangeMode: boolean = false;

  /** Initial range start (range mode only). */
  @Input() fromDate: NgbDate;

  /** Initial range end (range mode only). */
  @Input() toDate: NgbDate;

  /** Minimum allowed range length in days (range mode only). Shorter end dates are disabled. */
  @Input() minRangeDays: number;

  /** Maximum allowed range length in days (range mode only). Longer end dates are disabled. */
  @Input() maxRangeDays: number;

  /**
   * Range mode: how many calendar months ahead a *start* date may be.
   * End dates can still extend up to {@link maxRangeDays} from the start.
   * Unset/0 leaves start dates unconstrained (aside from minDate).
   * Ignored when {@link maxStartDays} is set.
   */
  @Input() maxStartMonths: number;

  /**
   * Range mode: how many days ahead a *start* date may be (from today).
   * Takes precedence over {@link maxStartMonths} when both are set.
   */
  @Input() maxStartDays: number;

  @Output() closeDatePickerEvent = new EventEmitter<NgbDate>();
  @Output() dateSelected = new EventEmitter<NgbDate>();

  /** Emitted (range mode only) whenever the selected range changes. */
  @Output() rangeSelected = new EventEmitter<DateRange>();

  inputValue = "";
  valid: boolean = true;
  isOpen: boolean;
  today: NgbDate;
  todayAsDate: Date;
  limitBlockedDate: Date;
  /** Latest allowed start date when {@link maxStartDays} or {@link maxStartMonths} is set. */
  startMaxDate: NgbDate;
  outsideDays: string = "collapsed";
  hoveredDate: any;
  baseImagePath = environment.baseImagePath;

  /**
   * Bound to the inline calendar's `[markDisabled]`. In range mode it disables
   * end dates that would make the range shorter than {@link minRangeDays} or
   * longer than {@link maxRangeDays}. Reassigned to a fresh reference whenever
   * the range anchor changes so ng-bootstrap rebuilds the disabled days.
   */
  markDisabledFn: (date: NgbDateStruct) => boolean = (date: NgbDateStruct) => this.isRangeDisabled(date);

  constructor(private config: NgbDatepickerConfig, private calendar: NgbCalendar, private elRef: ElementRef) {
    this.today = calendar.getToday();
    this.todayAsDate = new Date(this.today.year, this.today.month, this.today.day);
  }

  ngOnInit() {
    if (!this.minDate) {
      this.minDate = this.calendar.getNext(this.today, "d", this.numberOfDaysToBlockOff);
    }
    if (this.date) {
      if (typeof this.date === "string") {
        // its a javascript date string lets parse it into a ngb date
        this.date = DateRange.createNgbDateFromString(this.date);
      }
      this.inputValue = `${DateRange.monthNames[this.date.month]} ${this.date.day}, ${this.date.year}`;
    }
    if (this.numberOfDaysToBlockOff > 0) {
      this.limitBlockedDate = DateRange.createDateFromNgbDate(this.minDate);
    }
    if (this.maxNumberOfDays) {
      this.maxDate = this.calendar.getNext(this.today, "d", this.maxNumberOfDays);
    }

    if (this.rangeMode) {
      this.fromDate = this.toNgbDate(this.fromDate);
      this.toDate = this.toNgbDate(this.toDate);
    }

    if (this.maxStartDays) {
      this.startMaxDate = this.calendar.getNext(this.today, "d", this.maxStartDays);
    } else if (this.maxStartMonths) {
      this.startMaxDate = this.calendar.getNext(this.today, "m", this.maxStartMonths);
    }

    this.updateCalendarMaxDate();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.rangeMode) {
      return;
    }

    const fromChanged = changes.fromDate && !changes.fromDate.firstChange;
    const toChanged = changes.toDate && !changes.toDate.firstChange;
    if (!fromChanged && !toChanged) {
      return;
    }

    this.fromDate = this.toNgbDate(this.fromDate);
    this.toDate = this.toNgbDate(this.toDate);
    this.updateCalendarMaxDate();
    this.markDisabledFn = (d: NgbDateStruct) => this.isRangeDisabled(d);
    this.navigateToSelectedEnd();
  }

  ngAfterViewInit() {
    this.navigateToSelectedEnd();
  }

  /** Scroll the inline calendar to the month that contains the selected end date. */
  private navigateToSelectedEnd() {
    if (!this.toDate || !this.datepicker?.navigateTo) {
      return;
    }
    this.datepicker.navigateTo({ year: this.toDate.year, month: this.toDate.month });
  }

  /** Coerce a possible string/NgbDate input into an NgbDate (or null). */
  private toNgbDate(value: NgbDate | string | any): NgbDate {
    if (!value) {
      return null;
    }
    if (typeof value === "string") {
      return DateRange.createNgbDateFromString(value);
    }
    return value;
  }

  // we will need to use this function for machine availability so we can disable the days its not available
  isDisabled(date: NgbDateStruct) {
    return false;
  }

  // this is for the blocking the first few days for order processing
  isBlockedOff(date: NgbDateStruct) {
    const yesterday = this.calendar.getNext(this.today, "d", -1);

    // Block today if it's past 9am local time
    if (this.blockByHours) {
      const isToday = date.year === this.today.year && date.month === this.today.month && date.day === this.today.day;
      if (isToday) {
        const now = new Date();
        if (now.getHours() >= 9) {
          return true;
        }
      }
    }

    if (this.numberOfDaysToBlockOff > 0 && this.minDate.after(date) && yesterday.before(date)) {
      return true;
    }

    if (this.blockWeekends) {
      const isWeekend = [6, 7].includes(this.calendar.getWeekday(new NgbDate(date.year, date.month, date.day)));
      if (isWeekend) {
        return true;
      }
    }

    return false;
  }

  isMaxDate(date: NgbDateStruct) {
    // we do not have a max date so return false
    if (!this.maxDate) {
      return false;
    }
    const dateToCheck = NgbDate.from(date);
    if (dateToCheck.after(this.maxDate)) {
      return true;
    }
    return false;
  }

  openDatePicker() {
    this.isOpen = !this.isOpen;
    this.isOpen ? this.datepicker.open() : this.datepicker.close();
  }

  closeDatePicker() {
    this.datepicker?.close && this.datepicker.close();
  }

  onClosed() {
    this.isOpen = false;
    this.closeDatePickerEvent.emit(this.date);
  }

  // keeping this from now
  clearDatePicker() {
    this.date = null;
    this.inputValue = "";
  }

  isDateSelected(date: NgbDate) {
    return this.date != null && this.date.equals(date);
  }

  onDateSelection(date: NgbDate) {
    if (this.rangeMode) {
      this.onRangeDateSelection(date);
      return;
    }

    if (this.date != null && this.date.equals(date)) {
      this.verifyInputValue();
      this.closeDatePicker();
      this.dateSelected.emit(this.date); // Emit even when same date is selected
      return;
    }
    this.date = date;
    // for some reason we need to do this timeout, somewhere this date is getting set to a javascript string date.
    // sow we just need to reset it back to the ngbdate
    setTimeout(() => {
      this.date = date;
    }, 0);

    this.inputValue = `${DateRange.monthNames[this.date.month]} ${this.date.day}, ${this.date.year}`;
    this.closeDatePicker();
    this.onClosed();

    this.dateSelected.emit(this.date);
  }

  /**
   * Range-mode click handler. First click sets the start; a later click on a
   * valid later day sets the end. Once both dates are set, the next click is
   * always a new start. Clicking on or before the in-progress start also
   * begins a fresh range. Emits the current (possibly incomplete) range so the
   * parent can react immediately.
   */
  onRangeDateSelection(date: NgbDate) {
    const pickingEnd = !!(this.fromDate && !this.toDate);
    const settingEnd = pickingEnd && date.after(this.fromDate) && !this.isRangeLengthInvalid(this.fromDate, date);
    if (!settingEnd && this.isInvalidStartDate(date)) {
      return;
    }

    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } else if (settingEnd) {
      this.toDate = date;
    } else {
      this.toDate = null;
      this.fromDate = date;
    }

    // Reassign to a fresh reference so ng-bootstrap re-evaluates disabled days
    // against the new range anchor. Expand maxDate while picking an end date
    // so a 1-year rental can extend past the start-date window.
    this.updateCalendarMaxDate();
    this.markDisabledFn = (d: NgbDateStruct) => this.isRangeDisabled(d);
    this.rangeSelected.emit(new DateRange(this.fromDate, this.toDate));
  }

  /**
   * Range mode: disable candidate end dates that would make the rental shorter
   * than {@link minRangeDays} or longer than {@link maxRangeDays}. Days on or
   * before the chosen start stay enabled so the renter can restart the range,
   * unless they fall outside the start-date window ({@link maxStartDays} / {@link maxStartMonths}).
   * After a complete range, later days are candidate *start* dates again.
   */
  isRangeDisabled(date: NgbDateStruct): boolean {
    if (!this.rangeMode) {
      return false;
    }

    const ngbDate = NgbDate.from(date);

    if (this.fromDate && !this.toDate) {
      // Inclusive day count is 1 on the start day itself; keep that day (and
      // earlier days) enabled so the renter can restart the range — but only
      // when the click would still be a valid start.
      const days = this.daysBetween(this.fromDate, date);
      if (days <= 1) {
        return this.isInvalidStartDate(ngbDate);
      }

      return this.isRangeLengthInvalid(this.fromDate, date);
    }

    return this.isInvalidStartDate(ngbDate);
  }

  /**
   * True when `date` is later than the latest allowed rental start
   * ({@link maxStartDays} or {@link maxStartMonths} from today).
   */
  isInvalidStartDate(date: NgbDate): boolean {
    if (!this.startMaxDate || !date) {
      return false;
    }
    return date.after(this.startMaxDate);
  }

  /**
   * While choosing a start date, cap navigation at {@link startMaxDate}.
   * While choosing an end date, cap at start + {@link maxRangeDays} so a
   * 1-year rental is still selectable.
   */
  updateCalendarMaxDate() {
    if (!this.rangeMode || !this.startMaxDate) {
      return;
    }

    if (this.fromDate && !this.toDate) {
      const rangeDays = this.maxRangeDays || 365;
      this.maxDate = this.calendar.getNext(this.fromDate, "d", rangeDays - 1);
      return;
    }

    this.maxDate = this.startMaxDate;
    // Keep a previously selected end visible when editing a full-year rental.
    if (this.toDate && this.toDate.after(this.startMaxDate)) {
      this.maxDate = this.toDate;
    }
  }

  private isRangeLengthInvalid(from: NgbDateStruct, to: NgbDateStruct): boolean {
    const days = this.daysBetween(from, to);
    if (this.minRangeDays && days < this.minRangeDays) {
      return true;
    }
    if (this.maxRangeDays && days > this.maxRangeDays) {
      return true;
    }
    return false;
  }

  /** Inclusive day count (both endpoints count). Aug 4 → Aug 6 is 3 days. */
  private daysBetween(from: NgbDateStruct, to: NgbDateStruct): number {
    const fromDate = new Date(from.year, from.month - 1, from.day);
    const toDate = new Date(to.year, to.month - 1, to.day);
    return Math.round((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  }

  /** True when the day is a range endpoint, inside the range, or in the hover preview. */
  isRangeDay(date: NgbDate): boolean {
    return (
      date.equals(this.fromDate) ||
      (this.toDate != null && date.equals(this.toDate)) ||
      this.isInside(date) ||
      this.isHovered(date)
    );
  }

  /** True for days strictly between the selected start and end. */
  isInside(date: NgbDate): boolean {
    return this.toDate != null && date.after(this.fromDate) && date.before(this.toDate);
  }

  /** True for days in the live hover preview while an end date is being chosen. */
  isHovered(date: NgbDate): boolean {
    return this.fromDate != null && this.toDate == null && this.hoveredDate != null && date.after(this.fromDate) && date.before(this.hoveredDate);
  }

  // this is to verify that the input value is showing the formatted date
  // there is a case when you go to a page and already have a date preselected
  // if you then select the same exact date it does not format it properly
  verifyInputValue() {
    if (this.datePickerInput && this.datePickerInput.nativeElement?.value !== this.inputValue) {
      this.datePickerInput.nativeElement.value = this.inputValue;
    }
  }
  // since we are using a custom day template
  // we need to determine if each day is an outside day
  // if it is an outside day lets add the outside-day css class
  isOutsideDay(currentMonthShown: number, date: NgbDate) {
    return currentMonthShown !== date.month;
  }

  // really not happy with this hack...
  // this is to fix the fancy arrow when we used the 'collapsed' for outside days for ng bootstrap
  // since we dont want a lot of spacing in the date picker we have to do this hack of adding a class depending on the height of the monht
  // because we are using the collapsed date picker. if we did not do this, the fancy arrow would be in the wrong place.
  monthsToFixHeight = [
    { year: 2021, month: 1, class: "small-height" },
    { year: 2021, month: 5, class: "small-height" },
    { year: 2021, month: 10, class: "small-height" },
    { year: 2022, month: 1, class: "small-height" },
    { year: 2022, month: 7, class: "small-height" },
    { year: 2022, month: 10, class: "small-height" },
    { year: 2023, month: 4, class: "small-height" },
    { year: 2023, month: 7, class: "small-height" },
    { year: 2023, month: 12, class: "small-height" },
    { year: 2024, month: 3, class: "small-height" },
    { year: 2024, month: 6, class: "small-height" },
    { year: 2024, month: 3, class: "small-height" },
    { year: 2024, month: 6, class: "small-height" },
    { year: 2025, month: 3, class: "small-height" },
    { year: 2025, month: 8, class: "small-height" },
    { year: 2025, month: 11, class: "small-height" },
    { year: 2026, month: 2, class: "super-small-height" },
    { year: 2026, month: 5, class: "small-height" },
    { year: 2026, month: 8, class: "small-height" },
    { year: 2027, month: 1, class: "small-height" },
    { year: 2027, month: 5, class: "small-height" },
    { year: 2027, month: 10, class: "small-height" },
    { year: 2028, month: 1, class: "small-height" },
    { year: 2028, month: 4, class: "small-height" },
    { year: 2028, month: 7, class: "small-height" },
    { year: 2028, month: 12, class: "small-height" },
    { year: 2029, month: 9, class: "small-height" },
    { year: 2029, month: 12, class: "small-height" },
    { year: 2030, month: 3, class: "small-height" },
    { year: 2030, month: 6, class: "small-height" },
  ];

  navigate(event) {
    if (!event.next) {
      return;
    }
    const html = this.elRef.nativeElement;
    const arrow = html.querySelector(".dropdown-arrow") as HTMLElement;
    const mapping = this.monthsToFixHeight.find(d => d.year == event.next.year && d.month === event.next.month);
    if (mapping) {
      arrow?.classList.add(mapping.class);
    } else {
      arrow?.classList.remove("small-height");
      arrow?.classList.remove("super-small-height");
    }
  }
  // the reason for doing the arrow loaded is to do it on the first time the date picker is opened
  // all other consecutive navigation occurs in the navigate call back
  arrowLoaded() {
    const event = { next: this.date != null ? this.date : this.today };
    this.navigate(event);
  }
}
